import { useEffect, useRef, useState } from "react";
import "./submit-progress.css";

// Смуга кроків подання документа в ДПС (report-submit.js: progress у поданні). Кожен крок — що відбувається, лічильник,
// скільки триває зараз і скільки зазвичай (медіана з минулих подань компанії, typical). Затягнувся — чесно пишемо, що
// кабінет ДПС відповідає повільно й нічого не втрачено. Таймер — з часу початку кроку, який записав сервер
const LOAD = [
  ["cnt", "Номер документа в періоді"],
  ["new", "Документ у кабінеті ДПС"],
  ["files", "Копії документів"],
  ["main", "Головний документ"],
  ["save", "ДПС перевіряє комплект"],
  ["check", "Чекаємо результат перевірки"],
  ["p7s_get", "Документи на підпис"],
];
const SEND = [
  ["p7s", "Підпис документів у кабінеті"],
  ["send", "Надсилання в ДПС"],
  ["receipt", "Квитанція ДПС"],
];
const mmss = (ms) => { const s = Math.max(0, Math.round(ms / 1000)); return s >= 3600 ? `${Math.floor(s / 3600)} год ${Math.floor((s % 3600) / 60)} хв` : `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`; };
const mb = (b) => (b / 1048576).toLocaleString("uk-UA", { maximumFractionDigits: 1, minimumFractionDigits: 1 });
const hhmm = (t) => new Date(t).toLocaleTimeString("uk-UA", { hour: "2-digit", minute: "2-digit" });

// phase — "load" (завантаження в кабінет) або "send" (підпис, надсилання, квитанція)
export default function SubmitProgress({ sub, phase }) {
  const p = sub?.progress, [now, setNow] = useState(Date.now());
  const live = Boolean(p?.step) && !["done", "prepared"].includes(p.step) && (["creating", "signing", "sending", "sent"].includes(sub.state));
  useEffect(() => { if (!live) return; const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, [live]);
  if (!p?.steps) return null;
  const list = phase === "load" ? LOAD : SEND;
  // Показуємо лише кроки, які були або є зараз; майбутні — коли відомо, що вони будуть (усі, крім «копій» без додатків)
  const order = list.map(([k]) => k), curIdx = order.indexOf(p.step);
  const failed = ["failed", "unknown", "rejected"].includes(sub.state);
  const items = list.filter(([k], i) => p.steps[k] || i > curIdx && !(k === "files" && !p.steps.files && curIdx >= order.indexOf("main")) && !(k === "check" && !p.steps.check && curIdx > order.indexOf("check")));
  return (
    <ol className="sp" aria-label={phase === "load" ? "Завантаження в кабінет ДПС" : "Підпис і надсилання"}>
      {items.map(([k, label]) => {
        const st = p.steps[k], cur = p.step === k && live;
        const done = st?.end && !cur, bad = failed && p.step === k;
        const typ = sub.typical?.[k];
        const took = st?.end ? st.end - st.start : st ? now - st.start : null;
        let detail = null;
        if (k === "files" && (cur || done) && p.total) detail = `${cur ? p.done ?? 0 : p.total} з ${p.total}${p.bytesTotal ? ` · ${mb(cur ? p.bytesDone ?? 0 : p.bytesTotal)} з ${mb(p.bytesTotal)} МБ` : ""}`;
        if ((k === "p7s" || k === "p7s_get") && (cur || done) && p.total) detail = `${cur ? p.done ?? 0 : p.total} з ${p.total}`;
        const slow = cur && took != null && (typ?.p90 ? took > typ.p90 * 1.5 : took > (k === "receipt" ? 20 * 60e3 : 60e3));
        return (
          <li key={k} className={`sp-step ${bad ? "sp-bad" : cur ? "sp-cur" : done ? "sp-done" : "sp-todo"}`} aria-current={cur ? "step" : undefined}>
            <span className="sp-mark" aria-hidden="true">{bad ? "!" : done ? "✓" : cur ? "" : "·"}</span>
            <span className="sp-body">
              <span className="sp-label">{label}{detail && <span className="sp-count"> · {detail}</span>}</span>
              {cur && k !== "receipt" && <span className="sp-time">{mmss(took)}{typ?.median ? ` · зазвичай до ${mmss(typ.p90 || typ.median)}` : ""}</span>}
              {cur && k === "receipt" && <span className="sp-time">надіслано {mmss(took)} тому{typ?.median ? ` · квитанція зазвичай за ${mmss(typ.median)}` : ""}{sub.nextCheckAt ? ` · наступна перевірка о ${hhmm(sub.nextCheckAt)}` : ""}</span>}
              {done && took != null && k !== "receipt" && <span className="sp-time">{mmss(took)}</span>}
              {slow && <span className="sp-slow">Довше, ніж зазвичай: кабінет ДПС відповідає повільно. Нічого не втрачено — продовжуємо.</span>}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

// Остаточна квитанція: заголовок вкладки («✓ Прийнято ДПС — …» / «✗ Не прийнято ДПС — …») до повернення на вкладку й
// сповіщення браузера, якщо вкладка прихована й дозвіл є
const WAS_LIVE = ["sent", "unknown", "sending", "signing", "creating"];
function notifyReceipt(id, ok, what) {
  const title = ok ? `✓ Прийнято ДПС — ${what}` : `✗ Не прийнято ДПС — ${what}`;
  const base = document.title.replace(/^[✓✗] .*? \| /, "");
  document.title = `${title} | ${base}`;
  try { if (document.hidden && "Notification" in window && Notification.permission === "granted") new Notification(title, { body: ok ? "Квитанція ДПС отримана" : "Причина — у квитанції в eCabinet", tag: `sub-${id}` }); } catch { /* без сповіщень */ }
  const back = () => { if (!document.hidden) { document.title = base; document.removeEventListener("visibilitychange", back); } };
  if (document.hidden) document.addEventListener("visibilitychange", back); else setTimeout(() => { document.title = base; }, 15000);
}
// Одне подання (сторінка подання)
export function useReceiptNotice(sub, what) {
  const prev = useRef(sub?.state);
  useEffect(() => {
    const was = prev.current; prev.current = sub?.state;
    if (sub && ["accepted", "rejected"].includes(sub.state) && WAS_LIVE.includes(was)) notifyReceipt(sub.id, sub.state === "accepted", what);
  }, [sub?.state]);
}
// Список чернеток із поданнями (СМКОР): { id, state, nmr? } — сповіщення для кожного, чий стан став остаточним
export function useReceiptNotices(list, whatOf) {
  const prev = useRef(null);
  useEffect(() => {
    if (!Array.isArray(list)) return;
    const was = prev.current; prev.current = Object.fromEntries(list.map((d) => [d.id, d.state]));
    if (!was) return;
    for (const d of list) if (["accepted", "rejected"].includes(d.state) && WAS_LIVE.includes(was[d.id])) notifyReceipt(d.id, d.state === "accepted", whatOf(d));
  }, [list]);
}
// Дозвіл на сповіщення — лише у відповідь на дію людини (кнопка «Підписати й відправити»)
export const askNotifications = () => { try { if ("Notification" in window && Notification.permission === "default") Notification.requestPermission(); } catch { /* немає */ } };
